import React, { useCallback, useEffect } from 'react';
import {
  AppButton,
  Box,
  Controller,
  FieldWrapper,
  FormProvider,
  Label,
  Preloader,
  RawTextInput,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { Modal } from '@/components';
import { WarnModal } from '@/components/elements';
import { TriggerCategory, TriggerCategoryType } from '@/modules/HealthPlan/HealthPlan.types.ts';

import useAddTriggerForm from './hooks/useAddTriggerForm';
import useAddTriggerSubmit from './hooks/useAddTriggerSubmit';

const TRIGGER_TYPE_OPTIONS = [
  { key: TriggerCategory.FAMILY, value: 'Family', title: 'Family' },
  { key: TriggerCategory.EMBEDDED, value: 'Embedded', title: 'Embedded' },
];

interface Props {
  visible: boolean;
  defaultTriggerType: TriggerCategoryType;
  onClose: () => void;
  onCreated: (triggerId: string, triggerType: TriggerCategoryType) => void;
}

const AddTriggerModal: React.FC<Props> = ({ visible, defaultTriggerType, onClose, onCreated }) => {
  const { methods } = useAddTriggerForm({ defaultTriggerType });
  const { handleSubmit, isLoading, isServerError, setServerError } = useAddTriggerSubmit({
    onCreated,
    onClose,
  });

  const {
    control,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (visible) {
      reset({ triggerName: '', triggerType: defaultTriggerType });
    }
  }, [visible, defaultTriggerType, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <>
      <Modal visible={visible} onSetVisible={handleClose}>
        <FormProvider {...methods}>
          <Box direction="column" gap="spacing24">
            <Box direction="row" align="center" justify="center">
              <Text size="2xl" color="textTitle" weight="bold">
                Add New Trigger
              </Text>
            </Box>
            <Box background="module" round="moduleRound" pad="spacing24">
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border2' }}
                round="container1Round"
                pad="spacing24"
                gap="spacing16"
              >
                <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                  <Label showRequireIcon name="triggerName" isNarrow>
                    Trigger name
                  </Label>
                  <Box width="320px">
                    <Controller
                      name="triggerName"
                      control={control}
                      render={({ field }) => (
                        <RawTextInput
                          testId="add-trigger-modal_name-input"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Enter trigger name"
                          errorText={errors.triggerName?.message}
                        />
                      )}
                    />
                  </Box>
                </FieldWrapper>
                <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                  <Label showRequireIcon name="triggerType" isNarrow>
                    Type
                  </Label>
                  <Box width="320px">
                    <Controller
                      name="triggerType"
                      control={control}
                      render={({ field }) => (
                        <SelectDropdown
                          testId="add-trigger-modal_type-dropdown"
                          options={TRIGGER_TYPE_OPTIONS}
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          placeholder="Select type"
                          singleMode
                          id="triggerType"
                          name="triggerType"
                          errorText={errors.triggerType?.message}
                        />
                      )}
                    />
                  </Box>
                </FieldWrapper>
              </Box>
            </Box>
            <Box direction="row" align="center" justify="end" gap="s">
              <AppButton
                testId="add-trigger-modal_cancel-button"
                buttonType="secondary"
                width="control"
                onClick={handleClose}
              >
                Cancel
              </AppButton>
              <AppButton
                testId="add-trigger-modal_submit-button"
                width="control"
                onClick={onSubmit}
                disabled={isLoading}
              >
                {isLoading ? <Preloader color="white" /> : <Text>Submit</Text>}
              </AppButton>
            </Box>
          </Box>
        </FormProvider>
      </Modal>
      <WarnModal
        visible={isServerError}
        onSetVisible={() => setServerError(false)}
        header={SERVER_ERROR_MODAL_TITLE}
        helptext={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        onSubmit={onSubmit}
        submitButtonText="Try Again"
      />
    </>
  );
};

export default AddTriggerModal;
